interface IBidDateItem {
  date: string;
  price: number;
}

interface IBidDateSimulItem extends IBidDateItem {
  days: number;
  rate: number;
  highlight?: string;
}

interface ISeniorInfo {
  row1_1: string;
  row1_2: string;
  row2_1: string;
  row2_2: number;
  row3_1: number;
  row3_2: number;
  row4: number;
  row5: number;
}

function getFormatDate(date: Date): string {
  return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
}

export class NplCalculation {
  private appraisePrc: number;
  private marketPrc: number;
  private claimPrc: number;
  private statExpectBidPrc: number;
  private aiExpectBidPrc: number;
  private bidDateList: { date: string; price: number; highlight?: 'Y' | 'N' }[];
  private mortgagePrc: number;
  private court: string;
  private seniorInfo: ISeniorInfo;
  private pledgeRate: number;

  /** 연체이자율 */
  private static readonly DELAY_INTEREST_RATE = 8;

  /** 질권 이자율 */
  private static readonly PLEDGE_INTEREST_RATE = 9;

  /** 질권 인지세 */
  private static readonly STAMP_TAX = 175000;

  /** 저감률 */
  private static readonly REDUCTION_RATE_LIST: { [key: string]: number } = {
    울산지방법원: 30,
    창원지방법원: 20,
    '광주지방법원  본원': 30,
    '대구지방법원  본원': 30,
    '대전지방법원  본원': 30,
    '부산지방법원  본원': 20,
    '수원지방법원  본원': 30,
    '인천지방법원  본원': 30,
    '전주지방법원  본원': 30,
    '제주지방법원  본원': 30,
    '청주지방법원  본원': 20,
    '춘천지방법원  본원': 30,
    '광주지방법원  목포지원': 30,
    '광주지방법원  순천지원': 30,
    '광주지방법원  장흥지원': 20,
    '광주지방법원  해남지원': 30,
    '대구지방법원  경주지원': 30,
    '대구지방법원  김천지원': 30,
    '대구지방법원  상주지원': 30,
    '대구지방법원  서부지원': 30,
    '대구지방법원  안동지원': 30,
    '대구지방법원  영덕지원': 30,
    '대구지방법원  의성지원': 30,
    '대구지방법원  포항지원': 30,
    '대전지방법원  공주지원': 30,
    '대전지방법원  논산지원': 30,
    '대전지방법원  서산지원': 30,
    '대전지방법원  천안지원': 30,
    '대전지방법원  홍성지원': 30,
    '부산지방법원  동부지원': 20,
    '부산지방법원  서부지원': 20,
    '수원지방법원  성남지원': 30,
    '수원지방법원  안산지원': 30,
    '수원지방법원  안양지원': 20,
    '수원지방법원  여주시원': 30,
    '수원지방법원  여주지원': 30,
    '수원지방법원  평택지원': 30,
    '인천지방법원  부천지원': 30,
    '전주지방법원  군산지원': 30,
    '전주지방법원  남원지원': 30,
    '전주지방법원  정읍지원': 30,
    '창원지방법원  거창지원': 20,
    '창원지방법원  마산지원': 20,
    '창원지방법원  밀양지원': 20,
    '창원지방법원  진주지원': 20,
    '창원지방법원  통영지원': 20,
    '청주지방법원  영동지원': 20,
    '청주지방법원  제천지원': 20,
    '청주지방법원  충주지원': 20,
    '춘천지방법원  강릉지원': 30,
    '춘천지방법원  속초지원': 30,
    '춘천지방법원  영월지원': 30,
    '춘천지방법원  원주지원': 30,
    '의정부지방법원  본원': 30,
    '의정부지방법원  고양지원': 30,
    '의정부지방법원  남양주지원': 30,
    '서울남부지방법원  본원': 20,
    '서울동부지방법원  본원': 20,
    '서울북부지방법원  본원': 20,
    '서울서부지방법원  본원': 20,
    '서울중앙지방법원  본원': 20,
  };

  /**
   * @param appraisePrc 감정가
   * @param marketPrc 시세가격
   * @param claimPrc 청구금액
   * @param statExpectBidPrc (통계) 예상낙찰가격
   * @param aiExpectBidPrc (AI) 예상낙찰가격
   * @param bidDateList 매각기일 목록
   * @param mortgagePrc 근저당권설정액(말소기준)
   * @param bidCost 경매비용
   * @param court 법원
   */
  constructor(
    appraisePrc: number,
    claimPrc: number,
    statExpectBidPrc: number,
    aiExpectBidPrc: number,
    bidDateList: IBidDateItem[],
    mortgagePrc: number,
    court: string,
    seniorInfo: ISeniorInfo,
    isCompany: 'Y' | 'N',
    marketPrc: number,
  ) {
    this.appraisePrc = appraisePrc;
    this.claimPrc = claimPrc;
    this.statExpectBidPrc = statExpectBidPrc;
    this.aiExpectBidPrc = aiExpectBidPrc;
    this.bidDateList = bidDateList;
    this.mortgagePrc = mortgagePrc;
    this.court = court;
    this.seniorInfo = seniorInfo;
    this.pledgeRate = isCompany === 'Y' ? 90 : 75;

    this.marketPrc = marketPrc
      ? marketPrc
      : Math.round(
          (Math.max(this.statExpectBidPrc, this.aiExpectBidPrc) * 100) / 80,
        );
  }

  /** 시세가격 배율 */
  private get marketPrcRatio() {
    return (this.marketPrc / this.appraisePrc) * 100;
  }

  /** 청구금액 배율 */
  private get claimPrcRatio() {
    return (this.claimPrc / this.appraisePrc) * 100;
  }

  /** 통계 예상낙찰가율 */
  private get statExpectBidPrcRatio() {
    return (this.statExpectBidPrc / this.appraisePrc) * 100;
  }

  /** AI 예상낙찰가율 */
  private get aiExpectBidPrcRatio() {
    return (this.aiExpectBidPrc / this.appraisePrc) * 100;
  }

  /** 예상낙찰가격 배율 */
  private get expectBidPrcRatio() {
    const std = Math.max(this.statExpectBidPrcRatio, this.aiExpectBidPrcRatio);

    if (std + 10 > 100) {
      return std;
    }

    if (this.marketPrc > this.appraisePrc) {
      return std + 10;
    } else {
      return std;
    }
  }

  /** 예상낙찰가 */
  private get expectBidPrc() {
    return (this.appraisePrc * this.expectBidPrcRatio) / 100;
  }

  /** 매각기일 일자 */
  private get bidDate() {
    const today = new Date();
    let date = getFormatDate(today);

    for (let item of this.bidDateList) {
      if (
        item.date >= getFormatDate(today) &&
        item.price <= this.expectBidPrc
      ) {
        date = item.date;
        item.highlight = 'Y';
        break;
      }
    }

    const bidDateItem = this.bidDateList.find((item) => item.highlight === 'Y');

    if (!bidDateItem) {
      const last = this.bidDateList[this.bidDateList.length - 1];
      let newDate = new Date(last.date);
      let newPrice = last.price;

      while (newDate < today || newPrice > this.expectBidPrc) {
        newDate.setDate(newDate.getDate() + 35);
        newPrice = Math.round(
          newPrice * (1 - NplCalculation.REDUCTION_RATE_LIST[this.court] / 100),
        );

        date = getFormatDate(newDate);

        this.bidDateList.push({
          date,
          price: newPrice,
        });
      }

      this.bidDateList[this.bidDateList.length - 1].highlight = 'Y';
    }

    return date;
  }

  /** 투자기간 */
  private get investDays() {
    const now = new Date();
    const bidDate = new Date(this.bidDate);
    const futureDate = new Date(bidDate.setDate(bidDate.getDate() + 75));

    const diffInDays = Math.ceil(
      (futureDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
    );

    return diffInDays;
  }

  /** 선순위 합계금액 */
  private get seniorSumPrc() {
    const { row2_2, row3_1, row3_2, row4, row5 } = this.seniorInfo;
    return row2_2 + row3_1 + row3_2 + row4 + row5;
  }

  /** 대출원금 */
  private get loanPrc() {
    return this.mortgagePrc / 1.2;
  }

  /** 연체이자 금액 */
  private get delayInterestPrc() {
    return (
      (this.claimPrc * NplCalculation.DELAY_INTEREST_RATE * this.investDays) /
      365 /
      100
    );
  }

  /** 대출원리금합계 */
  private get loanSumPrc() {
    return this.claimPrc + this.delayInterestPrc;
  }

  /** 배당준비액 */
  private get dividendPreparationPrc() {
    return this.expectBidPrc - this.seniorSumPrc;
  }

  /** 대출채권 청구(연체이자 포함) 가능금액 */
  private get claimableLoanPrc() {
    return Math.min(this.mortgagePrc, this.loanSumPrc);
  }

  /** 본건 예상 배당액 */
  private get expectedDividendPrc() {
    return Math.min(this.dividendPreparationPrc, this.claimableLoanPrc);
  }

  /** 비용분석 - 경매비용 예상낙찰가 0.5% */
  private get bidCost() {
    return this.expectBidPrc * 0.005;
  }

  /** 부실채권 (대출잔액 + 미수이자) 청구금액 기준 할인율 */
  private get discountRate() {
    this.bidDate.toString();
    // const prevBidItem = this.bidDateList.find((item) => item.highlight === 'Y');
    let discountRate = 0;

    for (let i = 1; i <= 100; i++) {
      // 부실채권 할인금액
      const discountPrc = Math.round((this.claimPrc * i) / 100);

      // 부실채권 매입금액
      const purchasePrc = this.claimPrc - discountPrc;

      // if (prevBidItem && purchasePrc >= prevBidItem.price) {
      //   continue;
      // }

      // 질권대출금액
      const pledgeLoanPrc =
        Math.floor((purchasePrc * this.pledgeRate) / 100 / 1000000) * 1000000;

      // 질권이자비용
      const pledgeInterest =
        (pledgeLoanPrc *
          NplCalculation.PLEDGE_INTEREST_RATE *
          this.investDays) /
        365 /
        100;

      // 질권취급수수료
      const pledgeFee = pledgeLoanPrc * 0.005;

      // 선비용1
      const preCost1 = NplCalculation.STAMP_TAX + purchasePrc * 0.015;

      // 선비용2
      const preCost2 = purchasePrc * 0.005;

      // a안 소계
      const aTotal = purchasePrc + preCost1 + preCost2;

      // 기초자본
      const basicCapital = aTotal - pledgeLoanPrc;

      // b안 소계
      const bTotal =
        pledgeInterest + NplCalculation.STAMP_TAX + pledgeFee + basicCapital;

      const bidCost = this.bidCost;

      const bSum = bTotal + bidCost;

      const bRevenue =
        this.expectedDividendPrc +
        this.seniorInfo.row2_2 * 0.95 -
        bSum -
        pledgeLoanPrc;

      if ((((bRevenue / bSum) * 365) / this.investDays) * 100 >= 10) {
        discountRate = i;
        break;
      }
    }

    return discountRate;
  }

  /** 부실채권 할인금액 */
  private get discountPrc() {
    return (this.claimPrc * this.discountRate) / 100;
  }

  /** 부실채권 매입금액 */
  private get purchasePrc() {
    return this.claimPrc - this.discountPrc;
  }

  /** 선비용 */
  private get preCost1() {
    return NplCalculation.STAMP_TAX + this.purchasePrc * 0.015;
  }

  /** 선비용2 */
  private get preCost2() {
    return this.purchasePrc * 0.005;
  }

  /** a안 소계 */
  private get aTotal() {
    return this.purchasePrc + this.preCost1 + this.preCost2;
  }

  /** a안 총 투자비용 */
  private get aTotalInvestmentCost() {
    return this.aTotal + this.bidCost;
  }

  /** 질권대출금액 */
  private get pledgeLoanPrc() {
    return (
      Math.floor((this.purchasePrc * this.pledgeRate) / 100 / 1000000) * 1000000
    );
  }

  /** 질권이자비용 */
  private get pledgeInterest() {
    return (
      (this.pledgeLoanPrc *
        NplCalculation.PLEDGE_INTEREST_RATE *
        this.investDays) /
      365 /
      100
    );
  }

  /** 질권취급수수료 */
  private get pledgeFee() {
    return this.pledgeLoanPrc * 0.005;
  }

  /** 기초자본 */
  private get basicCapital() {
    return this.aTotal - this.pledgeLoanPrc;
  }

  /** b안 소계 */
  private get bTotal() {
    return (
      this.pledgeInterest +
      NplCalculation.STAMP_TAX +
      this.pledgeFee +
      this.basicCapital
    );
  }

  /** b안 총 투자비용 */
  private get bTotalInvestmentCost() {
    return this.bTotal + this.bidCost;
  }

  getSummary() {
    return {
      row1: this.appraisePrc,
      row2_1: this.marketPrc,
      row2_2: Math.round(this.marketPrcRatio * 10) / 10,
      row3_1: this.claimPrc,
      row3_2: Math.round(this.claimPrcRatio * 10) / 10,
      row4_1: Math.round(this.expectBidPrc),
      row4_2: Math.round(this.expectBidPrcRatio * 10) / 10,
      row5_1: this.statExpectBidPrc,
      row5_2: Math.round(this.statExpectBidPrcRatio * 10) / 10,
      row6_1: this.aiExpectBidPrc,
      row6_2: Math.round(this.aiExpectBidPrcRatio * 10) / 10,
    };
  }

  getExpectedLoan() {
    return {
      row1_1: this.mortgagePrc,
      row1_2: NplCalculation.DELAY_INTEREST_RATE,
      row2_1: Math.round(this.loanPrc),
      row2_2: this.investDays,
      row3_1: this.claimPrc,
      row3_2: Math.round(this.delayInterestPrc),
      row4: Math.round(this.loanSumPrc),
    };
  }

  getExpectedDividendAnalysis() {
    return {
      row1_1: this.expectBidPrc,
      row1_2: this.mortgagePrc,
      row2_1: this.seniorSumPrc,
      row2_2: Math.round(this.loanSumPrc),
      row3_1: Math.round(this.dividendPreparationPrc),
      row3_2: Math.round(this.claimableLoanPrc),
      row4: Math.round(this.expectedDividendPrc),
    };
  }

  getProfitAnalysis() {
    return {
      row1_1: this.mortgagePrc,
      row1_2: this.pledgeRate,
      row2_1: Math.round(this.loanPrc),
      row2_2: this.pledgeLoanPrc,
      row3_1: this.claimPrc,
      row3_2: NplCalculation.PLEDGE_INTEREST_RATE,
      row4_1: this.discountRate,
      row4_3: Math.round(this.pledgeInterest),
      row4_2: Math.round(this.discountPrc),
      row5_1: Math.round(this.purchasePrc),
      row5_2: NplCalculation.STAMP_TAX,
      row6_1: Math.round(this.preCost1),
      row6_2: Math.round(this.pledgeFee),
      row7_1: Math.round(this.preCost2),
      row7_2: Math.round(this.basicCapital),
      row8_1: Math.round(this.aTotal),
      row8_2: Math.round(this.bTotal),
      row9: Math.round(this.bidCost),
      row10_1: Math.round(this.aTotalInvestmentCost),
      row10_2: Math.round(this.bTotalInvestmentCost),
    };
  }

  getRevenueAnalysis() {
    const aRevenue =
      this.expectedDividendPrc +
      this.seniorInfo.row2_2 * 0.95 -
      this.aTotalInvestmentCost;
    const bRevenue =
      this.expectedDividendPrc +
      this.seniorInfo.row2_2 * 0.95 -
      this.bTotalInvestmentCost -
      this.pledgeLoanPrc;

    return {
      row1: Math.round(this.expectedDividendPrc),
      row2: Math.round(this.seniorInfo.row2_2 * 0.95),
      row3_1: Math.round(this.aTotalInvestmentCost),
      row3_2: Math.round(this.bTotalInvestmentCost),
      row4: Math.round(this.pledgeLoanPrc),
      row5_1: Math.round(aRevenue),
      row5_2: Math.round(bRevenue),
      row6_1: Math.round((aRevenue / this.aTotalInvestmentCost) * 100),
      row6_2: Math.round((bRevenue / this.bTotalInvestmentCost) * 100),
      row7_1: Math.round(
        (((aRevenue / this.aTotalInvestmentCost) * 365) / this.investDays) *
          100,
      ),
      row7_2: Math.round(
        (((bRevenue / this.bTotalInvestmentCost) * 365) / this.investDays) *
          100,
      ),
    };
  }

  getBidDateSimul() {
    this.bidDate.toString();

    return this.bidDateList.map((item, idx) => {
      if (idx > 0) {
        const prev = this.bidDateList[idx - 1];
        const date = new Date(item.date);
        const diffInDays = Math.ceil(
          (date.getTime() - new Date(prev.date).getTime()) /
            (1000 * 60 * 60 * 24),
        );
        const reductionRate = ((prev.price - item.price) / prev.price) * 100;

        return {
          date: item.date,
          days: diffInDays,
          price: item.price,
          rate: Math.round(reductionRate * 10) / 10,
          highlight: item.highlight || 'N',
        };
      }

      return {
        date: item.date,
        days: 0,
        price: item.price,
        rate: 0,
        highlight: 'Y',
      };
    });
  }

  getSeniorInfo() {
    return { ...this.seniorInfo, row6: this.seniorSumPrc };
  }

  getExpectedInvestmentDays() {
    const bidDate = new Date(this.bidDate);
    const futureDate = new Date(bidDate.setDate(bidDate.getDate() + 75));

    return {
      row1_1: getFormatDate(new Date()),
      row1_2: getFormatDate(futureDate),
      row2_1: this.bidDate,
      row2_2: this.investDays,
    };
  }
}

// const test1 = new NplCalculation(
//   1205000000,
//   860397703,
//   747100000,
//   698900000,
//   [
//     { date: '2024-03-18', price: 1205000000 },
//     { date: '2024-12-02', price: 964000000 },
//     { date: '2025-01-06', price: 771200000 },
//     { date: '2025-02-10', price: 616960000 },
//     { date: '2025-03-17', price: 493568000 },
//   ],
//   983000000,
//   '청주지방법원  충주지원',
//   {
//     row1_1: '1순위 근저당권',
//     row1_2: '없음',
//     row2_1: '없음',
//     row2_2: 3464375,
//     row3_1: 0,
//     row3_2: 0,
//     row4: 0,
//     row5: 0,
//   },
//   'Y',
//   933875000,
// );

// console.log(test1.getSummary());
// console.log(test1.getExpectedLoan());
// console.log(test1.getExpectedDividendAnalysis());
// console.log(test1.getProfitAnalysis());
// console.log(test1.getRevenueAnalysis());
// console.log(test1.getBidDateSimul());
